// =====================================================================================
// WebSocket Event Utilities  
// =====================================================================================
// Pure functions for creating standardized WebSocket event payloads and handling
// WebSocket operations. Extracted from useRoomConnection for reusability.

import type { Socket } from 'socket.io-client';

// =====================================================================================
// EVENT PAYLOAD FACTORIES
// =====================================================================================

/**
 * Creates a user status change payload
 * @param roomId - ID of the current room
 * @param status - New status (available, busy, dnd)
 * @returns Standardized status change payload
 */
export function createStatusChangePayload(roomId: string, status: string) {
  return {
    roomId,
    status
  };
}

/**
 * Creates a player teleport payload
 * @param targetUserId - ID of the user to teleport
 * @param position - Target position coordinates
 * @param roomId - ID of the current room
 * @returns Standardized teleport payload
 */
export function createTeleportPayload(
  targetUserId: string, 
  position: { x: number; y: number }, 
  roomId: string
) {
  return {
    targetUserId,
    position,
    roomId
  };
}

/**
 * Creates a private area door toggle payload
 * @param roomId - ID of the current room
 * @param areaId - ID of the private area
 * @returns Standardized door toggle payload
 */
export function createDoorTogglePayload(roomId: string, areaId: string) {
  return {
    roomId,
    areaId
  };
}

/**
 * Creates a join room payload
 * @param roomId - ID of the room to join
 * @returns Standardized join room payload
 */
export function createJoinRoomPayload(roomId: string) {
  return { roomId };
}

// =====================================================================================
// WEBSOCKET HELPERS
// =====================================================================================

/**
 * Safely sends a WebSocket message with error handling
 * @param socket - WebSocket connection
 * @param event - Event name to send
 * @param data - Payload data
 * @param options - Optional configuration
 * @returns True if message was sent, false if connection not ready
 */
export function safeEmit(
  socket: Socket | null, 
  event: string, 
  data: any,
  options: {
    logNonMovement?: boolean;
    requireConnection?: boolean;
  } = {}
): boolean {
  const { logNonMovement = true, requireConnection = true } = options;
  
  if (!socket || (requireConnection && !socket.connected)) {
    // Log connection issues for important events
    if (event !== 'player_movement' && logNonMovement) {
      console.warn('⚠️ WS: Cannot send %s - not connected', event);
    }
    return false;
  }
  
  // Only log non-movement events to reduce console spam
  if (event !== 'player_movement' && logNonMovement) {
    console.info('🚀 WS EMIT: %s', event);
  }
  
  socket.emit(event, data);
  return true;
}

/**
 * Creates a WebSocket event handler with standard error handling
 * @param handler - The actual handler function
 * @param eventName - Name of the event for logging
 * @returns Wrapped handler with error boundary
 */
export function createEventHandler<T = any>(
  handler: (data: T) => void,
  eventName: string
): (data: T) => void {
  return (data: T) => {
    try {
      console.info('📥 WS RECV: %s', eventName);
      handler(data);
    } catch (error) {
      console.error('❌ WS: Error handling %s event:', eventName, error);
    }
  };
}

/**
 * Creates a comprehensive event cleanup function
 * @param socket - WebSocket connection
 * @param eventHandlers - Map of event names to handler functions
 * @returns Cleanup function that removes all handlers
 */
export function createEventCleanup(
  socket: Socket,
  eventHandlers: Record<string, (...args: any[]) => void>
): () => void {
  return () => {
    Object.entries(eventHandlers).forEach(([eventName, handler]) => {
      socket.off(eventName, handler);
    });
  };
}

// =====================================================================================
// CONNECTION UTILITIES
// =====================================================================================

/**
 * Standard WebSocket connection configuration
 * @param serverUrl - WebSocket server URL
 * @param token - JWT authentication token
 * @param config - Optional connection configuration overrides
 * @returns Socket.IO connection options
 */
export function createConnectionConfig(
  serverUrl: string,
  token: string,
  config: {
    reconnectionDelay?: number;
    reconnectionDelayMax?: number;
    timeout?: number;
  } = {}
) {
  return {
    auth: { token },
    transports: ['websocket', 'polling'] as const,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: config.reconnectionDelay ?? 1000,
    reconnectionDelayMax: config.reconnectionDelayMax ?? 5000,
    timeout: config.timeout ?? 20000
  };
}

/**
 * Creates a timeout promise for WebSocket operations
 * @param timeoutMs - Timeout in milliseconds
 * @param errorMessage - Error message if timeout occurs
 * @returns Promise that rejects after timeout
 */
export function createTimeoutPromise(timeoutMs: number, errorMessage: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
  });
}

/**
 * Validates WebSocket connection state for operations
 * @param socket - WebSocket connection to check
 * @param requireRoom - Whether a room connection is required
 * @param currentRoom - Current room data (if requireRoom is true)
 * @returns Validation result with error message if invalid
 */
export function validateConnection(
  socket: Socket | null,
  requireRoom: boolean = false,
  currentRoom?: any
): { isValid: boolean; error?: string } {
  if (!socket) {
    return { isValid: false, error: 'No WebSocket connection' };
  }
  
  if (!socket.connected) {
    return { isValid: false, error: 'WebSocket not connected' };
  }
  
  if (requireRoom && !currentRoom) {
    return { isValid: false, error: 'Not connected to a room' };
  }
  
  return { isValid: true };
}

// =====================================================================================
// ERROR HANDLING
// =====================================================================================

/**
 * Standard WebSocket error codes and messages
 */
export const WEBSOCKET_ERRORS = {
  ACCESS_DENIED: 'ACCESS_DENIED',
  ROOM_NOT_FOUND: 'ROOM_NOT_FOUND',
  CONNECTION_TIMEOUT: 'CONNECTION_TIMEOUT',
  UNAUTHORIZED: 'UNAUTHORIZED'
} as const;

/**
 * Converts WebSocket error data to user-friendly error message
 * @param errorData - Error data from WebSocket event
 * @returns User-friendly error message
 */
export function formatWebSocketError(errorData: {
  code?: string;
  data?: { message?: string };
}): string {
  const { code, data } = errorData;
  
  switch (code) {
    case WEBSOCKET_ERRORS.ACCESS_DENIED:
      return 'Access denied to this room';
    case WEBSOCKET_ERRORS.ROOM_NOT_FOUND:
      return 'Room not found';
    case WEBSOCKET_ERRORS.UNAUTHORIZED:
      return 'Authentication required';
    case WEBSOCKET_ERRORS.CONNECTION_TIMEOUT:
      return 'Connection timeout';
    default:
      return data?.message || `Connection error: ${code || 'Unknown error'}`;
  }
}